import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
    IoBookmark, IoTrash, IoChevronDown, IoChevronUp,
    IoSchool, IoCheckmarkCircle, IoTrendingUp,
} from 'react-icons/io5';
import { FaMagic } from 'react-icons/fa';

const DIFFICULTY_COLORS = {
    Beginner: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
    Intermediate: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    Advanced: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
    Mixed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};

export default function IdeaCard({
    idea,
    isSaved = false,
    isAlreadySaved = false,
    showTrending = false,
    onCreateCourse,
    onSave,
    onDelete,
    isLoggedIn,
}) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const ideaId = idea._id || idea.id;
    const difficulty = idea.difficulty || 'Beginner';
    const duration = idea.estimatedDuration || idea.duration;
    const topics = idea.topics || [];
    const objectives = idea.learningObjectives || [];

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
        >
            <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                        {idea.category && (
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                                {idea.category}
                            </span>
                        )}
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-1 leading-snug">
                            {idea.title}
                        </h3>
                    </div>
                    {showTrending && idea.trendingScore > 0 && (
                        <span className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 shrink-0">
                            <IoTrendingUp className="w-3.5 h-3.5" />
                            {Math.round(idea.trendingScore)}
                        </span>
                    )}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-3">
                    {idea.description || idea.whyRelevant}
                </p>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${DIFFICULTY_COLORS[difficulty] || DIFFICULTY_COLORS.Beginner}`}>
                        {difficulty}
                    </span>
                    {duration && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                            {duration}
                        </span>
                    )}
                    {idea.pedagogyFramework && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                            {idea.pedagogyFramework}
                        </span>
                    )}
                </div>

                {topics.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {topics.slice(0, expanded ? topics.length : 3).map((topic) => (
                            <span
                                key={topic}
                                className="px-2 py-0.5 text-xs bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded border border-gray-200 dark:border-gray-600"
                            >
                                {topic}
                            </span>
                        ))}
                        {!expanded && topics.length > 3 && (
                            <span className="text-xs text-gray-400">+{topics.length - 3}</span>
                        )}
                    </div>
                )}

                <AnimatePresence>
                    {expanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            {objectives.length > 0 && (
                                <div className="mb-3">
                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5 flex items-center gap-1">
                                        <IoCheckmarkCircle className="w-3.5 h-3.5" />
                                        {t('courseIdeas.card.objectives', 'Learning Objectives')}
                                    </p>
                                    <ul className="space-y-1">
                                        {objectives.map((obj) => (
                                            <li key={obj} className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-1.5">
                                                <span className="text-blue-500 mt-0.5">•</span>
                                                {obj}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {idea.expectedOutcome && (
                                <div className="mb-3">
                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                                        {t('courseIdeas.card.outcome', 'Expected Outcome')}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-300">{idea.expectedOutcome}</p>
                                </div>
                            )}
                            {idea.evidenceBasis && (
                                <div className="mb-3 p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-1 mb-1">
                                        <IoSchool className="w-3.5 h-3.5" />
                                        {t('courseIdeas.card.evidence', 'Evidence-Based Design')}
                                    </p>
                                    <p className="text-xs text-blue-600 dark:text-blue-400">{idea.evidenceBasis}</p>
                                </div>
                            )}
                            {idea.targetAudience && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    <span className="font-medium">{t('courseIdeas.card.audience', 'Audience')}:</span> {idea.targetAudience}
                                </p>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-4"
                >
                    {expanded
                        ? t('courseIdeas.card.showLess', 'Show less')
                        : t('courseIdeas.card.showMore', 'Show details')}
                    {expanded ? <IoChevronUp className="w-3.5 h-3.5" /> : <IoChevronDown className="w-3.5 h-3.5" />}
                </button>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onCreateCourse(idea)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-colors text-sm font-semibold shadow-sm shadow-indigo-500/20"
                    >
                        <FaMagic className="w-3.5 h-3.5" />
                        {t('courseIdeas.actions.buildCourse')}
                    </button>
                    {isLoggedIn && (
                        isSaved ? (
                            <button
                                type="button"
                                onClick={() => onDelete(ideaId)}
                                className="px-3 py-2.5 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                                title={t('courseIdeas.actions.delete', 'Delete')}
                            >
                                <IoTrash className="w-5 h-5" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onSave(idea)}
                                disabled={isAlreadySaved}
                                className="px-3 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors disabled:opacity-50"
                                title={isAlreadySaved
                                    ? t('courseIdeas.actions.alreadySaved', 'Already saved')
                                    : t('courseIdeas.actions.save', 'Save')}
                            >
                                <IoBookmark className={`w-5 h-5 ${isAlreadySaved ? 'text-blue-600' : ''}`} />
                            </button>
                        )
                    )}
                </div>
            </div>
        </motion.div>
    );
}
